import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import {
  Activity, AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  Clock, Loader2, Search, Shield, Zap, XCircle,
} from "lucide-react";

const SEV_ORDER = { critical: 0, warning: 1, info: 2, unknown: 3 };
const STATUS_LABEL = { unsolved: "Unsolved", partially_solved: "Partial", solved: "Solved" };
const CAT_COLORS = {
  database: "bg-orange-100 text-orange-700",
  network: "bg-rose-100 text-rose-700",
  auth: "bg-violet-100 text-violet-700",
  third_party: "bg-sky-100 text-sky-700",
  pipeline: "bg-amber-100 text-amber-700",
  ui: "bg-pink-100 text-pink-700",
  unknown: "bg-slate-100 text-slate-600",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function SystemHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("severity");
  const intervalRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/error-brain/system-health`);
      setData(res.data);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, 60000);
    return () => clearInterval(intervalRef.current);
  }, [fetchHealth]);

  const handleAcknowledgeAll = async () => {
    try {
      await axios.post(`${API}/error-brain/acknowledge-all`);
      fetchHealth();
    } catch {}
  };

  const handleMarkSolved = async (fp) => {
    try {
      await axios.post(`${API}/error-brain/mark-solved/${fp}`);
      setExpandedRow(null);
      fetchHealth();
    } catch {}
  };

  const handleResetBreaker = async (service) => {
    try {
      await axios.post(`${API}/error-brain/reset-circuit-breaker`, { service });
      fetchHealth();
    } catch {}
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="system-health-panel">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          <span className="text-sm text-slate-500">Loading System Health...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { overall_status, active_errors, self_healing_rate, last_error, circuit_breakers, grouped_patterns } = data;

  // Flatten patterns for table
  const allPatterns = [];
  for (const [, entries] of Object.entries(grouped_patterns?.categories || {})) {
    allPatterns.push(...entries);
  }

  // Filter + search
  const filtered = allPatterns.filter((p) => {
    if (catFilter !== "all" && p.category !== catFilter) return false;
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (searchTerm && !p.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Sort
  filtered.sort((a, b) => {
    if (sortBy === "severity") {
      const sa = a.status === "solved" ? 3 : (SEV_ORDER[a.severity] ?? SEV_ORDER.unknown);
      const sb = b.status === "solved" ? 3 : (SEV_ORDER[b.severity] ?? SEV_ORDER.unknown);
      if (sa !== sb) return sa - sb;
      return (b.times_seen || 0) - (a.times_seen || 0);
    }
    if (sortBy === "times_seen") return (b.times_seen || 0) - (a.times_seen || 0);
    if (sortBy === "last_seen") return (b.last_seen || "").localeCompare(a.last_seen || "");
    return 0;
  });

  const categories = [...new Set(allPatterns.map((p) => p.category))].sort();

  // Healing rate color
  const healColor = self_healing_rate.percentage >= 80 ? "text-emerald-600" : self_healing_rate.percentage >= 60 ? "text-amber-600" : "text-red-600";
  const healBg = self_healing_rate.percentage >= 80 ? "bg-emerald-50 border-emerald-200" : self_healing_rate.percentage >= 60 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";

  // Status pill
  const statusConfig = {
    green: { label: "All Systems Normal", bg: "bg-emerald-500", textClass: "text-white" },
    amber: { label: "Degraded — Monitoring", bg: "bg-amber-500", textClass: "text-white" },
    red: { label: "Critical Issue Active", bg: "bg-red-500 animate-pulse", textClass: "text-white" },
  };
  const pill = statusConfig[overall_status] || statusConfig.green;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="system-health-panel">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-slate-900 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800">System Health</h2>
            <p className="text-[10px] text-slate-400">Error Brain — live monitoring</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${pill.bg} ${pill.textClass}`} data-testid="system-health-status-pill">
          {pill.label}
        </div>
      </div>

      {/* 4 Metric Cards */}
      <div className="px-4 pb-3 grid grid-cols-2 gap-2" data-testid="system-health-cards">
        {/* Card 1: Active Errors */}
        <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid="card-active-errors">
          <p className="text-[10px] font-medium text-slate-500 mb-1">Active Errors</p>
          <p className="text-2xl font-black text-slate-800 leading-none">{active_errors.total}</p>
          <div className="flex items-center gap-2 mt-1.5">
            {active_errors.critical > 0 && <span className="text-[10px] font-bold text-red-600">{active_errors.critical} critical</span>}
            {active_errors.warning > 0 && <span className="text-[10px] font-bold text-amber-600">{active_errors.warning} warning</span>}
            {active_errors.info > 0 && <span className="text-[10px] text-slate-400">{active_errors.info} info</span>}
            {active_errors.total === 0 && <span className="text-[10px] text-emerald-500 font-medium">Clean</span>}
          </div>
        </div>

        {/* Card 2: Self-Healing Rate */}
        <div className={`rounded-xl border p-3 ${healBg}`} data-testid="card-self-healing">
          <p className="text-[10px] font-medium text-slate-500 mb-1">Self-Healing Rate</p>
          <div className="flex items-baseline gap-1">
            <p className={`text-2xl font-black leading-none ${healColor}`}>{self_healing_rate.percentage}%</p>
            <Zap className={`w-3 h-3 ${healColor}`} />
          </div>
          <p className="text-[10px] text-slate-400 mt-1">{self_healing_rate.solved_last_7d}/{self_healing_rate.total_last_7d} last 7d</p>
        </div>

        {/* Card 3: Last Error */}
        <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid="card-last-error">
          <p className="text-[10px] font-medium text-slate-500 mb-1">Last Error</p>
          {last_error ? (
            <>
              <p className="text-[11px] font-semibold text-slate-700 leading-snug line-clamp-2">{last_error.description}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${CAT_COLORS[last_error.category] || CAT_COLORS.unknown}`}>{last_error.category}</span>
                <span className="text-[9px] text-slate-400">{timeAgo(last_error.last_seen)}</span>
                <span className={`text-[9px] font-bold ${last_error.status === "solved" ? "text-emerald-500" : "text-amber-500"}`}>{STATUS_LABEL[last_error.status] || last_error.status}</span>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-emerald-500 font-medium">No errors recorded</p>
          )}
        </div>

        {/* Card 4: Circuit Breakers */}
        <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid="card-circuit-breakers">
          <p className="text-[10px] font-medium text-slate-500 mb-1">Circuit Breakers</p>
          {circuit_breakers.length === 0 ? (
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-[11px] font-medium text-emerald-600">All services healthy</span>
            </div>
          ) : (
            <div className="space-y-1">
              {circuit_breakers.map((cb, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-red-600 truncate">{cb.service}</span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-[10px] text-slate-500">Resets in {cb.resets_in_minutes}m</span>
                    <button onClick={() => handleResetBreaker(cb.service)} className="text-[9px] text-sky-500 font-bold hover:underline" data-testid={`reset-breaker-${i}`}>Reset</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Service Health Dots (Part 8) */}
      {data.service_health?.services && (
        <div className="px-4 pb-3" data-testid="service-health-dots">
          <p className="text-[10px] font-medium text-slate-500 mb-2">Service Status</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.service_health.services).map(([name, svc]) => {
              const dotColor = svc.status === "green" ? "bg-emerald-400" : svc.status === "amber" ? "bg-amber-400" : "bg-red-500";
              const label = name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
              return (
                <div key={name} className="flex items-center gap-1.5" data-testid={`svc-dot-${name}`}>
                  <span className={`w-2 h-2 rounded-full ${dotColor} ${svc.status === "red" ? "animate-pulse" : ""}`} />
                  <span className="text-[10px] text-slate-600">{label}</span>
                  {svc.response_time_ms != null && <span className="text-[9px] text-slate-400">{svc.response_time_ms}ms</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* API Key Status */}
      {data.api_key_status && data.api_key_status.length > 0 && (
        <div className="px-4 pb-3" data-testid="api-key-status">
          <p className="text-[10px] font-medium text-slate-500 mb-2">API Keys</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {data.api_key_status.map((k) => {
              // Check if this key's sweep source is in backoff/blocked
              const sourceMap = { FOURSQUARE_API_KEY: "foursquare", YELP_API_KEY: "yelp", GOOGLE_PLACES_API_KEY: "google_places", USPS_USER_ID: "usps" };
              const sourceName = sourceMap[k.key];
              const backoff = sourceName && data.sweep_backoff ? data.sweep_backoff.find(s => s.source === sourceName) : null;
              const isBlocked = backoff?.blocked;
              const isBackingOff = backoff && !backoff.blocked && backoff.failure_count > 0;
              const dotColor = isBlocked ? "bg-red-500 animate-pulse" : isBackingOff ? "bg-amber-400" : k.configured ? "bg-emerald-400" : "bg-amber-400";
              return (
                <div key={k.key} className="flex items-center gap-1.5" data-testid={`api-key-${k.key}`}>
                  <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                  <span className="text-[10px] text-slate-600">{k.name}</span>
                  {isBlocked && <span className="text-[9px] text-red-500 font-medium" data-testid={`backoff-blocked-${sourceName}`}>blocked — resets in {Math.ceil(backoff.block_remaining_seconds / 60)}min</span>}
                  {isBackingOff && !isBlocked && <span className="text-[9px] text-amber-500 font-medium" data-testid={`backoff-active-${sourceName}`}>backing off ({backoff.failure_count} fails)</span>}
                  {!k.configured && !isBlocked && !isBackingOff && <span className="text-[9px] text-amber-500 font-medium">missing</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sweep Source Backoff (non-keyed sources like OSM, Nominatim, Wikipedia) */}
      {data.sweep_backoff && data.sweep_backoff.length > 0 && (() => {
        const keyedSources = ["foursquare", "yelp", "google_places", "usps"];
        const nonKeyed = data.sweep_backoff.filter(s => !keyedSources.includes(s.source) && (s.failure_count > 0 || s.blocked));
        if (!nonKeyed.length) return null;
        return (
          <div className="px-4 pb-3" data-testid="sweep-backoff-status">
            <p className="text-[10px] font-medium text-slate-500 mb-2">Sweep Source Health</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {nonKeyed.map(s => (
                <div key={s.source} className="flex items-center gap-1.5" data-testid={`sweep-source-${s.source}`}>
                  <span className={`w-2 h-2 rounded-full ${s.blocked ? "bg-red-500 animate-pulse" : "bg-amber-400"}`} />
                  <span className="text-[10px] text-slate-600">{s.source.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  {s.blocked ? (
                    <span className="text-[9px] text-red-500 font-medium">blocked — resets in {Math.ceil(s.block_remaining_seconds / 60)}min</span>
                  ) : (
                    <span className="text-[9px] text-amber-500 font-medium">backing off ({s.failure_count} fails)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Data Freshness Summary */}
      {data.freshness && (
        <div className="px-4 pb-3" data-testid="freshness-summary">
          <p className="text-[10px] font-medium text-slate-500 mb-2">Data Freshness</p>
          <div className="flex items-center gap-2">
            {[
              { label: "Fresh", count: data.freshness.green, color: "bg-emerald-400", textColor: "text-emerald-700" },
              { label: "Aging", count: data.freshness.amber, color: "bg-amber-400", textColor: "text-amber-700" },
              { label: "Stale", count: data.freshness.red, color: "bg-red-400", textColor: "text-red-700" },
              { label: "Unscored", count: data.freshness.unscored, color: "bg-slate-300", textColor: "text-slate-500" },
            ].map(({ label, count, color, textColor }) => (
              <div key={label} className="flex items-center gap-1" data-testid={`freshness-${label.toLowerCase()}`}>
                <span className={`w-2 h-2 rounded-full ${color}`} />
                <span className={`text-[10px] ${textColor}`}>{label}</span>
                <span className="text-[10px] font-mono font-bold text-slate-600">{count?.toLocaleString() || 0}</span>
              </div>
            ))}
          </div>
          {data.freshness.total > 0 && (() => {
            const t = data.freshness.total;
            const gPct = Math.round((data.freshness.green / t) * 100);
            const aPct = Math.round((data.freshness.amber / t) * 100);
            const rPct = Math.round((data.freshness.red / t) * 100);
            return (
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1.5 flex" data-testid="freshness-bar">
                {gPct > 0 && <div className="h-full bg-emerald-400" style={{ width: `${gPct}%` }} />}
                {aPct > 0 && <div className="h-full bg-amber-400" style={{ width: `${aPct}%` }} />}
                {rPct > 0 && <div className="h-full bg-red-400" style={{ width: `${rPct}%` }} />}
              </div>
            );
          })()}
        </div>
      )}

      {/* Error Log Table */}
      <div className="border-t border-slate-100">
        {/* Filters */}
        <div className="px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="error-log-filters">
          <div className="relative flex-1 min-w-[120px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search errors..."
              className="w-full pl-8 pr-3 py-1.5 text-[11px] rounded-lg border border-slate-200 focus:border-sky-300 focus:outline-none"
              data-testid="error-search"
            />
          </div>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="text-[11px] px-2 py-1.5 rounded-lg border border-slate-200 bg-white" data-testid="error-cat-filter">
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-[11px] px-2 py-1.5 rounded-lg border border-slate-200 bg-white" data-testid="error-status-filter">
            <option value="all">All Status</option>
            <option value="unsolved">Unsolved</option>
            <option value="partially_solved">Partial</option>
            <option value="solved">Solved</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-[11px] px-2 py-1.5 rounded-lg border border-slate-200 bg-white" data-testid="error-sort">
            <option value="severity">Severity</option>
            <option value="times_seen">Times Seen</option>
            <option value="last_seen">Last Seen</option>
          </select>
        </div>

        {/* Table rows */}
        <div className="max-h-[400px] overflow-y-auto" data-testid="error-log-table">
          {filtered.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400" data-testid="error-log-empty">
              {allPatterns.length === 0 ? "No error patterns recorded yet" : "No matches"}
            </div>
          ) : (
            filtered.map((p) => {
              const isExpanded = expandedRow === p.fingerprint;
              const rowBg = p.status === "solved"
                ? "bg-emerald-50/30"
                : p.severity === "critical" || p.escalated
                  ? "bg-red-50/50"
                  : "bg-amber-50/30";

              return (
                <div key={p.fingerprint} className={`border-t border-slate-100 ${rowBg}`} data-testid={`error-row-${p.fingerprint?.substring(0, 8)}`}>
                  <button
                    onClick={() => setExpandedRow(isExpanded ? null : p.fingerprint)}
                    className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-slate-50/50 transition"
                    data-testid={`error-row-toggle-${p.fingerprint?.substring(0, 8)}`}
                  >
                    {/* Severity dot */}
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      p.status === "solved" ? "bg-emerald-400" :
                      p.severity === "critical" ? "bg-red-500 animate-pulse" :
                      "bg-amber-400"
                    }`} />
                    {/* Category */}
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${CAT_COLORS[p.category] || CAT_COLORS.unknown}`}>{p.category}</span>
                    {/* Description */}
                    <span className="text-[11px] text-slate-700 flex-1 truncate">{p.description}</span>
                    {/* Times seen */}
                    <span className="text-[10px] font-bold text-slate-500 flex-shrink-0">{p.times_seen}x</span>
                    {/* Status */}
                    <span className={`text-[9px] font-bold flex-shrink-0 ${
                      p.status === "solved" ? "text-emerald-500" : p.status === "partially_solved" ? "text-sky-500" : "text-amber-500"
                    }`}>{STATUS_LABEL[p.status] || p.status}</span>
                    {/* Expand icon */}
                    {isExpanded ? <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                  </button>

                  {/* Expanded Detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-3 pt-1 space-y-2" data-testid={`error-detail-${p.fingerprint?.substring(0, 8)}`}>
                          <div className="grid grid-cols-2 gap-2 text-[10px]">
                            <div><span className="text-slate-400">First seen:</span> <span className="text-slate-600">{timeAgo(p.first_seen)}</span></div>
                            <div><span className="text-slate-400">Last seen:</span> <span className="text-slate-600">{timeAgo(p.last_seen)}</span></div>
                            <div><span className="text-slate-400">Times seen:</span> <span className="font-bold text-slate-700">{p.times_seen}</span></div>
                            <div><span className="text-slate-400">Best solution confidence:</span> <span className="font-bold text-slate-700">{((p.best_solution_confidence || 0) * 100).toFixed(0)}%</span></div>
                          </div>
                          {p.current_best_solution && (
                            <div className="text-[10px]">
                              <span className="text-slate-400">Best solution:</span> <span className="text-slate-700 font-medium">{p.current_best_solution}</span>
                            </div>
                          )}
                          {p.status !== "solved" && (
                            <button
                              onClick={() => handleMarkSolved(p.fingerprint)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-600 transition"
                              data-testid={`mark-solved-${p.fingerprint?.substring(0, 8)}`}
                            >
                              <CheckCircle className="w-3 h-3" /> Mark as Solved
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap gap-2" data-testid="system-health-actions">
        <button onClick={handleAcknowledgeAll} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[11px] font-semibold hover:bg-slate-200 transition" data-testid="acknowledge-all-btn">
          <CheckCircle className="w-3 h-3" /> Acknowledge All
        </button>
        <button onClick={fetchHealth} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 text-sky-600 text-[11px] font-semibold hover:bg-sky-100 transition" data-testid="refresh-health-btn">
          <Activity className="w-3 h-3" /> Refresh
        </button>
      </div>
    </div>
  );
}
